import type { ReactNode, RefObject } from "react";
import { BlockBoundary } from "./BlockBoundary";
import { ButtonBlock } from "./ButtonBlock";
import { CarouselBlock } from "./CarouselBlock";
import type { MediaResizeChange } from "./mediaSize";
import type { SectionTarget } from "./buttonSettings";
import { LibraryBlock } from "./LibraryBlock";
import { PageBlock } from "./PageBlock";
import type { PageCardStyle } from "./PageCard";
import { PictureBlock } from "./PictureBlock";
import { QuoteBlock } from "./QuoteBlock";
import { TextBlock } from "./TextBlock";
import { VideoBlock } from "./VideoBlock";
import { ContextMenu } from "./ContextMenu";
import type {
  BlockEditPatch,
  EditorAsset,
  EditorBlock,
  EditorPage,
} from "../editor.types";

type BlockRendererProps = {
  imageEditMode?: "crop" | "details";
  block: EditorBlock;
  blocks: EditorBlock[];
  pages: EditorPage[];
  assetsById: Map<string, EditorAsset>;
  onSelectPage: (pageId: string) => void;
  onDelete?: (blockId: string) => void;
  onSaveEdit?: (
    block: EditorBlock,
    patch: BlockEditPatch,
  ) => Promise<string | null>;
  onQuickUpdate?: (
    block: EditorBlock,
    patch: BlockEditPatch,
  ) => Promise<string | null>;
  onCancelEdit?: () => void;
  onSelectBlock?: (sourceBlockId: string) => void;
  onOpenPage?: (page: EditorPage) => void;
  pageHref?: (page: EditorPage) => string;
  selectedBlockId?: string | null;
  selectedBlockRef?: RefObject<HTMLDivElement | null>;
  currentPageId?: string;
  selected?: boolean;
  editing?: boolean;
  bare?: boolean;
  /** In the editor, whether this block is the selected one. */
  active?: boolean;
  /** A library's card style, applied to page blocks inside it. */
  pageDisplay?: PageCardStyle;
  compact?: boolean;
  /** The page's sections, which a button can link to. */
  sections?: SectionTarget[];
  /** In the editor: uploads to the site's images (for a carousel). */
  onUploadAsset?: (file: File) => Promise<EditorAsset>;
};

export const BlockRenderer = ({
  imageEditMode,
  block,
  blocks,
  pages,
  assetsById,
  onSelectPage,
  onDelete,
  onSaveEdit,
  onQuickUpdate,
  onCancelEdit,
  onSelectBlock,
  onOpenPage,
  pageHref,
  selectedBlockId,
  selectedBlockRef,
  currentPageId,
  selected,
  editing,
  bare,
  active,
  pageDisplay,
  compact,
  sections,
  onUploadAsset,
}: BlockRendererProps): ReactNode => {
  const openTarget =
    block.block_type === "page"
      ? pages.find(
          (page) => page.source_page_id === block.target_page_source_id,
        )
      : undefined;

  const editProps = {
    editing,
    onCancelEdit,
    onSave: onSaveEdit
      ? (patch: BlockEditPatch) => onSaveEdit(block, patch)
      : undefined,
  };

  // In the editor, a picture or video resized by its bars: a dragged width
  // replaces the size, and settling on a size clears the width.
  const onResize = onQuickUpdate
    ? (change: MediaResizeChange) => {
        const { width: _width, ...rest } = block.content as Record<string, unknown>;
        void onQuickUpdate(block, {
          content: ("size" in change ? { ...rest, size: change.size } : { ...rest, width: change.width }) as BlockEditPatch["content"],
        });
      }
    : undefined;
  const rendered = (() => {
    switch (block.block_type) {
      case "text":
        return <TextBlock block={block} {...editProps} />;
      case "quote":
        return <QuoteBlock block={block} {...editProps} />;
      case "page":
        return (
          <PageBlock
            assetsById={assetsById}
            block={block}
            currentPageId={currentPageId}
            href={openTarget ? pageHref?.(openTarget) : undefined}
            onOpen={
              openTarget && onOpenPage
                ? () => onOpenPage(openTarget)
                : undefined
            }
            pages={pages}
            display={pageDisplay}
            compact={compact}
            {...editProps}
          />
        );
      case "picture":
        return (
          <PictureBlock imageEditMode={imageEditMode} block={block} assetsById={assetsById} onResize={onResize} {...editProps} />
        );
      case "video":
        return (
          <VideoBlock
            block={block}
            selected={active}
            onResize={onResize}
            onSetLink={
              onQuickUpdate
                ? (url) =>
                    void onQuickUpdate(block, {
                      content: { ...(block.content as object), url },
                    })
                : undefined
            }
          />
        );
      case "button":
        return (
          <ButtonBlock
            block={block}
            pages={pages}
            sections={sections}
            pageHref={pageHref}
            onOpenPage={onOpenPage}
            onUpdate={
              onQuickUpdate
                ? (content) => void onQuickUpdate(block, { content: content as BlockEditPatch["content"] })
                : undefined
            }
          />
        );
      case "carousel":
        return (
          <CarouselBlock
            block={block}
            assetsById={assetsById}
            onUploadAsset={onUploadAsset}
            onUpdate={
              onQuickUpdate
                ? (content) => void onQuickUpdate(block, { content: content as BlockEditPatch["content"] })
                : undefined
            }
          />
        );
      case "library":
        return (
          <LibraryBlock
            assetsById={assetsById}
            bare={bare}
            block={block}
            blocks={blocks}
            currentPageId={currentPageId}
            onOpenPage={onOpenPage}
            pageHref={pageHref}
            onDelete={onDelete}
            onQuickUpdate={onQuickUpdate}
            onSaveEdit={onSaveEdit}
            onSelectBlock={onSelectBlock}
            onSelectPage={onSelectPage}
            pages={pages}
            selectedBlockId={selectedBlockId}
            selectedBlockRef={selectedBlockRef}
            {...editProps}
          />
        );
    }
  })();
  // In the editor (where blocks can be changed), a note in its place.
  const content = (
    <BlockBoundary key={block.id} note={Boolean(onQuickUpdate || onSaveEdit)}>
      {rendered}
    </BlockBoundary>
  );

  return selected ? (
    <ContextMenu
      onDelete={() => onDelete?.(block.id)}
      onOpen={openTarget ? () => onSelectPage(openTarget.id) : undefined}
    >
      {content}
    </ContextMenu>
  ) : (
    content
  );
};
